import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { type LucideIcon } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/AlertDialog'
import { DialogHeaderWithIcon } from '../components/ui/DialogHeaderWithIcon'
import { cn } from '../lib/utils'

type IconColor = 'teal' | 'amber' | 'rose' | 'slate' | 'violet' | 'emerald'

type ConfirmOptions = {
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  destructive?: boolean
  icon?: LucideIcon
  iconColor?: IconColor
}

type ConfirmState = ConfirmOptions & { open: boolean }

type ConfirmFunction = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFunction | undefined>(undefined)

interface ConfirmProviderProps {
  children: React.ReactNode
}

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children }) => {
  const [state, setState] = useState<ConfirmState | null>(null)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
      setState({ open: true, ...options })
    })
  }, [])

  const handleClose = (result: boolean) => {
    resolverRef.current?.(result)
    resolverRef.current = null
    setState(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={state?.open ?? false}
        onOpenChange={(open) => {
          if (!open) handleClose(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            {state?.icon ? (
              <DialogHeaderWithIcon
                icon={state.icon}
                iconColor={state.iconColor ?? 'teal'}
                title={state.title}
                description={state.description}
                variant="alertDialog"
              />
            ) : (
              <>
                <AlertDialogTitle>{state?.title}</AlertDialogTitle>
                {state?.description && (
                  <AlertDialogDescription>{state.description}</AlertDialogDescription>
                )}
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleClose(false)}>
              {state?.cancelText ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                state?.destructive &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
              onClick={() => handleClose(true)}
            >
              {state?.confirmText ?? 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}

export const useConfirm = (): ConfirmFunction => {
  const context = useContext(ConfirmContext)
  if (context === undefined) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return context
}
